/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';

const FIN_EMAIL_HITL_FF = 'fin-email-hitl';

type Option = 'on' | 'off';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (
    params: FinSetupBehaviorSaveParams,
    options?: {
      messages?: { success?: string; failure?: string };
      rethrow?: boolean;
    },
  ) => Promise<void> | void;
}

interface Signature {
  Args: Args;
}

export default class AskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  @tracked isSaving = false;

  get behavior(): Profile {
    return this.args.ruleset.rulesetLinks.firstObject.object;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_FF));
  }

  get options() {
    return [
      {
        value: 'off' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.description',
      },
      {
        value: 'on' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.description',
      },
    ];
  }

  get isEnabled(): boolean {
    return this.selectedOption === 'on';
  }

  get statusLabel(): string {
    return this.intl.t(
      this.isEnabled
        ? 'operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status'
        : 'operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status',
    );
  }

  get isInteractionDisabled(): boolean {
    return this.isSaving;
  }

  @action
  async setSelectedOption(option: Option) {
    if (this.isInteractionDisabled || option === this.selectedOption) {
      return;
    }

    let previousOption = this.selectedOption;
    this.selectedOption = option;
    this.isSaving = true;

    try {
      await this.args.partialSave(
        { hitl_settings: { enabled: option === 'on' } },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
    } catch {
      this.selectedOption = previousOption;
    } finally {
      this.isSaving = false;
    }
  }

  @action
  close(closeAccordion: () => void) {
    closeAccordion();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate': typeof AskATeammate;
    'operator/fin/setup/sections/ask-a-teammate': typeof AskATeammate;
  }
}
